import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import About from "./pages/About";
import Contact from "./pages/Contact";
import LegalPage from "./pages/LegalPage";
import Dashboard from "./pages/Dashboard";
import Breakdown from "./pages/Breakdown";
import ConstructedResponse from "./pages/ConstructedResponse";
import EssayStructure from "./pages/EssayStructure";
import RubricFeedback from "./pages/RubricFeedback";
import GuidedCoach from "./pages/GuidedCoach";
import Examples from "./pages/Examples";
import ParentHub from "./pages/ParentHub";
import Badges from "./pages/Badges";
import WritingPractice from "./pages/WritingPractice";
import GrammarLab from "./pages/GrammarLab";
import SATPrep from "./pages/SATPrep";
import RevisionIntelligence from "./pages/RevisionIntelligence";
import GrowthPaths from "./pages/GrowthPaths";
import Upgrade from "./pages/Upgrade";
import UpgradeSuccess from "./pages/UpgradeSuccess";
import RealWorldWriting from "./pages/RealWorldWriting";
import CrossCurricular from "./pages/CrossCurricular";
import WritingConference from "./pages/WritingConference";
import Roster from "./pages/Roster";
import KeywordLanding from "./pages/KeywordLanding";
import Faq from "./pages/Faq";
import { Blog, BlogPost } from "./pages/Blog";
import AdminSeo from "./pages/AdminSeo";
import AdminBlog from "./pages/AdminBlog";
import AdminAnalytics from "./pages/AdminAnalytics";
import TeacherPilot from "./pages/TeacherPilot";
import FeaturePublic from "./pages/FeaturePublic";
import FeedbackWidget from "./components/FeedbackWidget";
import Layout from "./components/Layout";

const Private = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

/**
 * Public-or-Private route: when the visitor is anonymous (or Googlebot),
 * render a crawlable SEO landing at the same URL. When logged in, render
 * the real feature inside the app Layout. Keeps the URL canonical for SEO.
 */
const PublicOrPrivate = ({ path, children }) => {
  const { user } = useAuth();
  if (user) return <Layout>{children}</Layout>;
  return <FeaturePublic path={path} />;
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <FeedbackWidget />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* SEO keyword landing pages */}
            <Route path="/essay-writing-help" element={<KeywordLanding />} />
            <Route path="/argumentative-writing-support" element={<KeywordLanding />} />
            <Route path="/constructed-response-strategies" element={<KeywordLanding />} />
            <Route path="/writing-intervention-tools" element={<KeywordLanding />} />
            <Route path="/student-writing-improvement" element={<KeywordLanding />} />
            <Route path="/ai-writing-feedback-for-students" element={<KeywordLanding />} />
            <Route path="/narrative-writing-support" element={<KeywordLanding />} />
            <Route path="/thesis-writing-help" element={<KeywordLanding />} />
            <Route path="/paragraph-organization-practice" element={<KeywordLanding />} />
            <Route path="/writing-revision-strategies" element={<KeywordLanding />} />
            {/* Admin (login-gated by the components themselves) */}
            <Route path="/admin/seo" element={<Private><AdminSeo /></Private>} />
            <Route path="/admin/blog" element={<Private><AdminBlog /></Private>} />
            <Route path="/admin/analytics" element={<Private><AdminAnalytics /></Private>} />
            <Route path="/teachers/pilot" element={<TeacherPilot />} />
            <Route path="/app" element={<Private><Dashboard /></Private>} />
            <Route path="/app/practice" element={<PublicOrPrivate path="/app/practice"><WritingPractice /></PublicOrPrivate>} />
            <Route path="/app/grammar" element={<PublicOrPrivate path="/app/grammar"><GrammarLab /></PublicOrPrivate>} />
            <Route path="/app/sat-ap" element={<PublicOrPrivate path="/app/sat-ap"><SATPrep /></PublicOrPrivate>} />
            <Route path="/app/revision" element={<Private><RevisionIntelligence /></Private>} />
            <Route path="/app/real-world" element={<Private><RealWorldWriting /></Private>} />
            <Route path="/app/cross-curricular" element={<Private><CrossCurricular /></Private>} />
            <Route path="/app/conference" element={<Private><WritingConference /></Private>} />
            <Route path="/app/roster" element={<Private><Roster /></Private>} />
            <Route path="/app/paths" element={<Private><GrowthPaths /></Private>} />
            <Route path="/app/upgrade" element={<Private><Upgrade /></Private>} />
            <Route path="/app/upgrade/success" element={<Private><UpgradeSuccess /></Private>} />
            <Route path="/app/essay" element={<PublicOrPrivate path="/app/essay"><EssayStructure /></PublicOrPrivate>} />
            <Route path="/app/breakdown" element={<Private><Breakdown /></Private>} />
            <Route path="/app/constructed" element={<Private><ConstructedResponse /></Private>} />
            <Route path="/app/rubric" element={<Private><RubricFeedback /></Private>} />
            <Route path="/app/coach" element={<Private><GuidedCoach /></Private>} />
            <Route path="/app/examples" element={<Private><Examples /></Private>} />
            <Route path="/app/badges" element={<Private><Badges /></Private>} />
            <Route path="/app/parent" element={<Private><ParentHub /></Private>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
